import React from 'react';
import { motion } from 'framer-motion';
import { Product } from '@/context/types';

// Define the columns mapped exactly to your NutritionFacts interface
const NUTRITION_COLUMNS = [
  { key: 'calories', label: 'Calories', unit: 'cal' },
  { key: 'totalFat', label: 'Fat', unit: 'g' },
  { key: 'saturatedFat', label: 'Sat. fat', unit: 'g' },
  { key: 'transFat', label: 'Trans fat', unit: 'g' },
  { key: 'cholesterol', label: 'Cholesterol', unit: 'mg' },
  { key: 'totalCarbohydrates', label: 'Carbs', unit: 'g' },
  { key: 'dietaryFiber', label: 'Fiber', unit: 'g' },
  { key: 'totalSugars', label: 'Sugars', unit: 'g' },
  { key: 'protein', label: 'Protein', unit: 'g' },
] as const;

interface NutritionTableProps {
  products: Product[];
  className?: string;
}

export const NutritionTable: React.FC<NutritionTableProps> = ({
  products,
  className = '',
}) => {
  // Framer Motion orchestration for staggered row entrance
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.05 },
    },
  };

  const rowVariants = {
    hidden: { opacity: 0, y: 10 },
    show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100 } },
  } as const;

  if (!products || products.length === 0) return null;

  return (
    <div
      className={`w-full bg-white rounded-[var(--radius-artisan)] shadow-sm border border-brand-truffle/10 overflow-hidden ${className}`}
    >
      <div className="w-full overflow-x-auto scrollbar-hide">
        {/* Removed the large min-w to allow the new compact columns to naturally size */}
        <table className="w-full table-auto text-left border-collapse">
          {/* --- TABLE HEADER --- */}
          <thead className="bg-brand-cream border-b border-brand-truffle/10 text-brand-truffle font-display text-[11px] md:text-xs tracking-wider">
            <tr>
              {/* Sticky first column header */}
              <th className="sticky left-0 z-20 bg-brand-cream px-4 py-4 w-[200px] sm:w-[250px] shadow-[1px_0_0_rgba(0,0,0,0.05)] align-bottom uppercase font-displaysticky left-0 z-20 bg-brand-cream px-2 sm:px-4 py-4 w-[140px] max-w-[140px] sm:w-[200px] sm:max-w-[200px] md:w-[250px] md:max-w-[250px] shadow-[1px_0_0_rgba(0,0,0,0.05)] align-bottom uppercase font-display">
                Product
              </th>

              {NUTRITION_COLUMNS.map((col) => (
                <th
                  key={col.key}
                  className="relative h-[120px] min-w-[70px] w-[70px] sm:min-w-[85px] sm:w-[85px] align-bottom"
                >
                  {/* Shifted the left origin slightly from 1/2 to 1/3 for better centering within the wider column */}
                  <div className="absolute bottom-2 left-1/3 origin-bottom-left -rotate-45 whitespace-nowrap">
                    {col.label}{' '}
                    <span className="text-brand-truffle/60">({col.unit})</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* --- TABLE BODY --- */}
          <motion.tbody
            variants={containerVariants}
            initial="hidden"
            animate="show"
            className="font-display font-[400] text-sm text-text-heading"
          >
            {products.map((product) => {
              const nutrition = product.nutritionFacts;
              if (!nutrition) return null;

              return (
                <motion.tr
                  key={product.id}
                  variants={rowVariants}
                  className="group border-b last:border-b-0 border-brand-truffle/5 odd:bg-white even:bg-brand-frost/70 hover:bg-brand-truffle/5 transition-colors duration-200"
                >
                  {/* Sticky Product Cell */}
                  <td className="sticky left-0 z-10 px-2 sm:px-4 py-3 bg-white group-odd:bg-white group-even:bg-brand-frost/70 group-hover:bg-[#F9F8F6] transition-colors duration-200 shadow-[1px_0_0_rgba(0,0,0,0.03)] w-[140px] max-w-[140px] sm:w-[200px] sm:max-w-[200px] md:w-[250px] md:max-w-[250px]">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className="w-7 h-7 sm:w-10 sm:h-10 shrink-0 rounded-full flex items-center justify-center overflow-visible">
                        {product.img?.url ? (
                          <motion.img
                            src={product.img.url}
                            alt={product.img.altText || product.title}
                            className="w-full h-full object-contain drop-shadow-[0_15px_15px_rgba(0,0,0,0.35)] will-change-transform pointer-events-auto"
                            loading="lazy"
                          />
                        ) : (
                          <span className="text-[8px] text-text-muted/50 uppercase tracking-widest">
                            Item
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col min-w-0">
                        <span className="font-[600] text-sm truncate text-brand-truffle leading-tight">
                          {product.title}
                        </span>
                        {nutrition.servingSize && (
                          <span className="hidden sm:block text-[10px] text-text-muted/80 mt-0.5">
                            Serving: {nutrition.servingSize.amount} (
                            {nutrition.servingSize.weight}g)
                          </span>
                        )}
                      </div>
                    </div>
                  </td>

                  {/* Compact Nutrition Data Cells */}
                  {NUTRITION_COLUMNS.map((col) => {
                    const value = nutrition[col.key as keyof typeof nutrition];

                    return (
                      <td
                        key={col.key}
                        className="px-2 py-3 text-center text-text-muted font-[500] text-[13px]"
                      >
                        {typeof value === 'number' ? value : '-'}
                      </td>
                    );
                  })}
                </motion.tr>
              );
            })}
          </motion.tbody>
        </table>
      </div>
    </div>
  );
};
